import React, { useState, useEffect } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import Button from "../../../Components/UI/buttonGlobal";
import { getSections } from "../../../MyApi/section";
import { useMutation, useQuery } from "react-query";
import { storeRatingKeys, showRatingKeys, updateRatingKeys } from "../../../MyApi/ratings";
import { toast } from "react-toastify";
import i18next from "i18next";
import SkeletonWrapper from "../../../Components/UI/skeletonWrapper";

// Function to generate key from label
const generateKeyFromLabel = (label) => {
  if (!label) return "";

  // Convert Arabic characters to English equivalents
  const arabicMap = {
    'أ': 'a', 'إ': 'i', 'آ': 'a', 'ا': 'a', 'ب': 'b', 'ت': 't', 'ث': 'th',
    'ج': 'j', 'ح': 'h', 'خ': 'kh', 'د': 'd', 'ذ': 'dh', 'ر': 'r', 'ز': 'z',
    'س': 's', 'ش': 'sh', 'ص': 's', 'ض': 'd', 'ط': 't', 'ظ': 'z', 'ع': 'a',
    'غ': 'gh', 'ف': 'f', 'ق': 'q', 'ك': 'k', 'ل': 'l', 'م': 'm', 'ن': 'n',
    'ه': 'h', 'و': 'w', 'ي': 'y', 'ى': 'a', 'ة': 'h'
  };

  return label
    .split('')
    .map(char => arabicMap[char] || char)
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_+/g, '_') // Remove multiple underscores
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
};

const RatingModal = ({ isOpen, toggle, refetch, modalMode, modalId }) => {
  const [formData, setFormData] = useState({
    section_id: null,
    label: "",
    key: "",
    is_active: 1,
  });
  const lang = i18next.language;

  const { data, isLoading: sectionsLoading } = useQuery("getSections", () => getSections());
  const sections = data?.data?.sections || [];

  const { data: showData, isLoading: showLoading } = useQuery(
    ["showRatingKeys", modalId],
    () => showRatingKeys(modalId),
    {
      enabled: !!modalId && (modalMode === "edit" || modalMode ==="show")
    }
  );

  const ratingKey = showData?.data?.data || {};

  const mutation = useMutation({
    mutationKey: ["postRatingKeys"],
    mutationFn: (posData) => storeRatingKeys(posData),
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      refetch();
      resetForm();
      toggle()
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        toast.error(Object.values(error.response.data.errors).flat().join(', '));
      } else {
        toast.error(`${error.response?.data?.message || "حدث خطأ ما"}`);
      }
    }
  });

  const updateMutation = useMutation({
    mutationKey: ["updateRatingKeys"],
    mutationFn: (postData) => updateRatingKeys(postData, modalId),
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      refetch();
      toggle()
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        toast.error(Object.values(error.response.data.errors).flat().join(', '));
      } else {
        toast.error(`${error.response?.data?.message || "حدث خطأ ما"}`);
      }
    }
  });

  const resetForm = () => {
    setFormData({
      section_id: null,
      label: "",
      key: "",
      is_active: 1,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };

    // Auto-generate key when label changes
    if (name === "label") {
      updatedFormData.key = generateKeyFromLabel(value);
    }

    setFormData(updatedFormData);
  };

  useEffect(() => {
    if ( (modalMode === "edit" || modalMode ==="show") && ratingKey && Object.keys(ratingKey).length > 0) {
      setFormData({
        section_id: ratingKey.section_id || null,
        label: ratingKey.label || "",
        key: ratingKey.key || "",
        is_active: ratingKey.is_active || 1,
      });
    }
  }, [ratingKey, modalMode]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const submitData = {
      section_id: formData.section_id,
      label: formData.label,
      key: formData.key,
      is_active: formData.is_active,

    };

    if (modalMode === "edit") {
      updateMutation.mutate({...submitData , _method:"PUT"});
    } else {
      mutation.mutate(submitData);
    }
  };

  const isDisabled = modalMode === "show";
  const isLoading =  mutation.isLoading || updateMutation.isLoading;

  const getModalTitle = () => {
    switch (modalMode) {
      case "edit":
        return "تعديل التقييم";
      case "show":
        return "عرض التقييم";
      default:
        return "إضافة تقييم جديد";
    }
  };

  const getSubmitButtonLabel = () => {
    switch (modalMode) {
      case "edit":
        return "تعديل التقييم";
      default:
        return "إضافة التقييم";
    }
  };
  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>{getModalTitle()}</ModalHeader>
      <ModalBody className="add">
        <div className="row">
          <div className="col-12 mb-3">
            <label htmlFor="section_id" className="form-label">النوع</label>
            <SkeletonWrapper isLoading={showLoading}>

            <select
              name="section_id"
              id="section_id"
              value={formData.section_id || ""}
              onChange={handleChange}
              className="form-select"
              disabled={sectionsLoading || isDisabled}
              >
              <option value="">اختر نوع التقييم</option>
              {sections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
              </SkeletonWrapper>
          </div>

          <div className="col-12 mb-3">
            <label htmlFor="label" className="form-label">اسم التقييم</label>
                     <SkeletonWrapper isLoading={showLoading}>

            <input
              type="text"
              name="label"
              id="label"
              value={formData.label}
              onChange={handleChange}
              className="form-control"
              placeholder="أدخل اسم التقييم"
              disabled={isDisabled}
              />
              </SkeletonWrapper>
          </div>

          <div className="col-12 mb-3">
            <label htmlFor="key" className="form-label">المفتاح</label>
                  <SkeletonWrapper isLoading={showLoading}>

            <input
              type="text"
              name="key"
              id="key"
              value={formData.key}
              onChange={handleChange}
              className="form-control"
              placeholder="سيتم إنشاؤه تلقائياً"
              disabled={isDisabled}
              />
              </SkeletonWrapper>
            <small className="text-muted">يتم إنشاء المفتاح تلقائياً من اسم التقييم</small>
          </div>
        </div>

        {modalMode !== "show" && (
          <div className="d-flex justify-content-start gap-2 mt-3">
            <Button
              className="btn-primary"
              label={getSubmitButtonLabel()}
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={isLoading}
            />
            <Button
              className="btn-white"
              label={"إلغاء"}
              onClick={toggle}
              disabled={isLoading}
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default RatingModal;
